import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import { buildToolFromAction } from '@atlassian/conversation-assistant-message-actions/common/utils/buildToolFromAction';
import { shouldUseResumption } from '@atlassian/conversation-assistant-message-actions/common/utils/shouldUseResumption';
import { isActionExecutedSuccessfully } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import {
	CHAT_STORE_STATUS,
	isStoreAssistant,
	isStoreError,
	isStoreHuman,
	type Conversation,
	type StoreAssistant,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type {
	BackgroundConversationActionsDescriptor,
	BackgroundConversationActionsLookup,
	BackgroundConversationActionsResolutionResult,
	ResolveBackgroundConversationActionsCommandProperties,
} from '@atlassian/rovo-content-bridge-api-commands/commands/background-conversation-actions-command';

import type { useChatStore } from '../../chat-store/useChatStore';

type ChatActions = ReturnType<typeof useChatStore>[1];
type Session = { sourceId: string; conversationId: string; agentId: string };
type RegisteredSession = Session & { acceptedMessages: Set<string>; lockedMessages: Set<string> };
type ConversationState = Pick<
	Conversation,
	'sourceId' | 'sourceAgentId' | 'lastAgentMessageStatus' | 'status'
>;

const MAX_SESSIONS = 20;
const hasString = (value: unknown): value is string =>
	typeof value === 'string' && value.trim().length > 0;
const hasData = (action: MessageAction, fields: string[]): boolean =>
	typeof action.data === 'object' &&
	action.data !== null &&
	fields.every((field) => hasString((action.data as Record<string, unknown>)[field]));

const commentActionForPublication = (
	action: MessageAction,
	resolution: 'confirmed' | 'cancelled',
): MessageAction => {
	if (resolution !== 'confirmed') return action;

	const data = action.data as Record<string, unknown>;
	const comment = data.comment;
	if (typeof comment !== 'string') return action;

	try {
		return { ...action, data: { ...data, comment: JSON.stringify(encodeAdf(comment)) } };
	} catch {
		// Preserve the previous behavior if ADF conversion fails
		return action;
	}
};

const canResume = (action: MessageAction): boolean =>
	shouldUseResumption(action) &&
	// Native Rovo tools have no tool-definition ID; confirmation uses their invocation ID.
	(action.toolSource === 'CONVO_AI' || hasString(action.actionId)) &&
	hasString(action.toolSource) &&
	hasString(action.humanMessageId);

const usesResumption = (action: MessageAction): boolean =>
	isExperimentEnabled('rovo_tools_resumption_migration_exp') && shouldUseResumption(action);

const pendingActions = (assistant: StoreAssistant): MessageAction[] =>
	(assistant.content.message.message.actions ?? []).filter((action) => {
		// Match the Chat renderer: edits without ADF were streamed into the editor already.
		if (
			action.key === ActionKeys.AifcAdfUpdate &&
			typeof action.data === 'object' &&
			action.data !== null &&
			!(action.data as Record<string, unknown>).adf
		) {
			return false;
		}
		const invoked = assistant.invokedActions?.[action.invocationId];
		return (
			(action.status === undefined || action.status === 'planned') &&
			(invoked === undefined || invoked === 'planned')
		);
	});

// Page comment actions never evaluate the gate.
const isCommentAction = (action: MessageAction): boolean =>
	action.key === ActionKeys.ConfluencePageComment ||
	(action.key === ActionKeys.ConfluenceComment && fg('canvas-rovo-comment-sessions'));

const isSupported = (action: MessageAction): boolean => {
	return (
		isCommentAction(action) &&
		hasString(action.invocationId) &&
		hasData(action, ['parentCommentId', 'contentId', 'comment']) &&
		(!usesResumption(action) || canResume(action))
	);
};

function latestAssistant(messages: StoreChatItem[]): StoreAssistant | undefined {
	for (let index = messages.length - 1; index >= 0; index--) {
		const message = messages[index];
		if (isStoreHuman(message) || isStoreError(message)) return undefined;
		if (isStoreAssistant(message)) return message;
	}
	return undefined;
}

/**
 * A finished action is recorded only after a successful confirmed execution; cancelled and failed
 * actions retain their distinct states. Restrict the decision to the latest human turn so a prior
 * reply, a continuation, or another tool can never hide the current Chat result after reload.
 */
function hasCompletedReplyOnlyLatestTurn(messages: StoreChatItem[], commentId: string): boolean {
	let latestHumanIndex = -1;
	for (let index = messages.length - 1; index >= 0; index--) {
		if (isStoreHuman(messages[index])) {
			latestHumanIndex = index;
			break;
		}
	}
	if (latestHumanIndex < 0) return false;

	const latestHuman = messages[latestHumanIndex];
	if (!isStoreHuman(latestHuman) || !hasString(latestHuman.content.id)) return false;
	const humanMessageId = String(latestHuman.content.id);
	let foundReply = false;

	for (let index = latestHumanIndex + 1; index < messages.length; index++) {
		const message = messages[index];
		if (isStoreError(message) || !isStoreAssistant(message)) return false;
		if (
			message.isAborted ||
			message.content.streamingComplete === false ||
			message.content.message.message.async_status === 'IN_PROGRESS'
		)
			return false;
		if (message.content.message.message.plugin_invocations?.length) return false;
		const actions = message.content.message.message.actions ?? [];
		for (const action of actions) {
			if (
				action.humanMessageId !== humanMessageId ||
				action.status !== 'finished' ||
				!isSupported(action) ||
				(action.data as Record<string, unknown>).parentCommentId !== commentId
			) {
				return false;
			}
			foundReply = true;
		}
	}

	return foundReply;
}

/** Same-tab approvals, with comment-bound history recovery after a reload. */
export class BackgroundConversationActionsCoordinator {
	private readonly sessions = new Map<string, RegisteredSession>();
	private readonly recoveries = new Map<string, Promise<void>>();
	private readonly terminalRecoveries = new Map<string, 'unavailable'>();
	private recoveryGeneration = 0;

	constructor(
		private readonly getMessages: (conversationId: string) => StoreChatItem[],
		private readonly actions: Pick<
			ChatActions,
			| 'sendMessageConfirmedTools'
			| 'resolveConversationAction'
			| 'warmConversationCache'
			| 'setConversationData'
		>,
		private readonly getConversation: (conversationId: string) => ConversationState | undefined,
	) {}

	register(session: Session): void {
		if (
			!hasString(session.sourceId) ||
			!hasString(session.conversationId) ||
			!hasString(session.agentId)
		)
			return;
		const existing = this.sessions.get(session.conversationId);
		if (existing) return;
		this.sessions.set(session.conversationId, {
			...session,
			acceptedMessages: new Set(),
			lockedMessages: new Set(),
		});
		// Keep only recent same-tab sessions; an evicted approval can still continue in Chat.
		const oldest = this.sessions.keys().next().value;
		if (this.sessions.size > MAX_SESSIONS && oldest) this.sessions.delete(oldest);
	}

	clear(): void {
		this.recoveryGeneration++;
		this.sessions.clear();
		this.recoveries.clear();
		this.terminalRecoveries.clear();
	}

	get(
		sourceId: string,
		conversationId: string,
		commentId?: string,
	): BackgroundConversationActionsLookup {
		const registered = this.sessions.get(conversationId);
		const conversation = this.getConversation(conversationId);
		if (!registered && commentId && hasString(sourceId) && hasString(conversationId)) {
			// After a reload, hydrate the read-only history before binding it to this comment.
			const recoveryKey = `${sourceId}:${conversationId}:${commentId}`;
			const terminalRecovery = this.terminalRecoveries.get(recoveryKey);
			if (terminalRecovery) return { status: terminalRecovery };
			if (conversation?.sourceId && conversation.sourceId !== sourceId) {
				return { status: 'unavailable' };
			}
			if (!this.recoveries.has(recoveryKey)) {
				const recovery = this.recover(sourceId, conversationId, commentId, this.recoveryGeneration);
				this.recoveries.set(recoveryKey, recovery);
				void recovery.finally(() => {
					if (this.recoveries.get(recoveryKey) === recovery) this.recoveries.delete(recoveryKey);
				});
			}
			return { status: 'loading' };
		}
		if (
			!registered ||
			registered.sourceId !== sourceId ||
			conversation?.sourceId !== sourceId ||
			conversation.lastAgentMessageStatus === 'FAILED'
		) {
			return { status: 'unavailable' };
		}
		// Session status can precede the final Chat message; wait before inspecting its actions.
		if (conversation.status !== CHAT_STORE_STATUS.idle) return { status: 'loading' };
		const messages = this.getMessages(conversationId);
		if (commentId && hasCompletedReplyOnlyLatestTurn(messages, commentId)) {
			return { status: 'completed' };
		}
		const assistant = latestAssistant(messages);
		if (assistant && registered.acceptedMessages.has(assistant.content.message.message.id)) {
			return { status: 'resolved' };
		}
		if (!assistant || assistant.content.streamingComplete === false) return { status: 'loading' };
		if (assistant.isAborted) return { status: 'unavailable' };
		const pending = pendingActions(assistant);
		if (!pending.length) return { status: 'resolved' };
		const descriptor = this.describe(registered);
		if (descriptor) return { status: 'ready', descriptor };
		const replies = pending.filter(isSupported);
		// A preview is not an approval descriptor. Mixed actions still resolve only in Chat.
		if (
			pending.length > 1 &&
			replies.length === 1 &&
			hasString(assistant.content.message.message.id)
		) {
			return {
				status: 'unavailable',
				review: {
					messageId: assistant.content.message.message.id,
					reply: JSON.parse(JSON.stringify(replies[0])),
				},
			};
		}
		return { status: 'unavailable' };
	}

	private async recover(
		sourceId: string,
		conversationId: string,
		commentId: string,
		generation: number,
	): Promise<void> {
		try {
			let failed = false;
			// Hydrate without selecting Chat or executing an action; explicit resolve remains separate.
			await this.actions.warmConversationCache({
				conversation: { id: conversationId },
				forceRefresh: true,
				onError: () => {
					failed = true;
				},
			});
			if (generation !== this.recoveryGeneration) return;
			// History fetch errors are retryable; the Confluence caller polls for a bounded window.
			if (failed) {
				// Some cache errors leave the store in loadingChat, which blocks the next fetch.
				if (this.getConversation(conversationId)?.status === CHAT_STORE_STATUS.loadingChat) {
					this.actions.setConversationData({
						conversationId,
						data: { status: CHAT_STORE_STATUS.idle },
					});
				}
				return;
			}
			const conversation = this.getConversation(conversationId);
			const messages = this.getMessages(conversationId);
			const assistant = latestAssistant(messages);
			if (
				conversation?.status !== CHAT_STORE_STATUS.idle ||
				(conversation.sourceId && conversation.sourceId !== sourceId) ||
				!assistant
			) {
				if (
					generation === this.recoveryGeneration &&
					conversation?.status === CHAT_STORE_STATUS.idle
				) {
					this.terminalRecoveries.set(`${sourceId}:${conversationId}:${commentId}`, 'unavailable');
				}
				return;
			}
			// In-progress history is retryable; do not cache it as a terminal recovery failure.
			if (
				messages.some(
					(message) =>
						isStoreAssistant(message) &&
						message.content.message.message.async_status === 'IN_PROGRESS',
				)
			)
				return;
			// Bind only history whose proposed action belongs to the requested comment.
			if (!this.hasMatchingCommentAction(messages, commentId)) {
				this.terminalRecoveries.set(`${sourceId}:${conversationId}:${commentId}`, 'unavailable');
				return;
			}
			const agentId = hasString(conversation.sourceAgentId)
				? conversation.sourceAgentId
				: assistant.content.message.message.author_id;
			if (!hasString(agentId)) {
				this.terminalRecoveries.set(`${sourceId}:${conversationId}:${commentId}`, 'unavailable');
				return;
			}
			this.actions.setConversationData({ conversationId, data: { sourceId } });
			this.register({ sourceId, conversationId, agentId });
		} catch {
			// Leave unexpected cache errors retryable as well.
		}
	}

	private hasMatchingCommentAction(messages: StoreChatItem[], commentId: string): boolean {
		return messages.some(
			(message) =>
				isStoreAssistant(message) &&
				(message.content.message.message.actions ?? []).some(
					(action) =>
						isCommentAction(action) &&
						typeof action.data === 'object' &&
						action.data !== null &&
						(action.data as Record<string, unknown>).parentCommentId === commentId,
				),
		);
	}

	async resolve(
		command: ResolveBackgroundConversationActionsCommandProperties,
	): Promise<BackgroundConversationActionsResolutionResult> {
		const session = this.session(command.sourceId, command.conversationId);
		const descriptor = session && this.describe(session);
		if (
			!session ||
			!descriptor ||
			descriptor.messageId !== command.messageId ||
			descriptor.revision !== command.revision ||
			!this.hasExactResolution(descriptor, command)
		) {
			return { status: 'stale' };
		}

		// Lock before the first await. Keep it after uncertain failures; recovery belongs in Chat.
		session.lockedMessages.add(descriptor.messageId);
		try {
			const action = descriptor.action;
			const resolution = command.resolution;
			const publicationAction = commentActionForPublication(action, resolution.resolution);
			if (usesResumption(action)) {
				return await new Promise<BackgroundConversationActionsResolutionResult>((complete) => {
					let accepted = false;
					void this.actions
						.sendMessageConfirmedTools({
							conversationId: session.conversationId,
							agentId: session.agentId,
							messageSources: {
								interactionSource: 'followUps',
								followUpSource: 'agentMentionComment',
							},
							confirmed_tools: {
								message_id: descriptor.messageId,
								tools: [buildToolFromAction(publicationAction, resolution.resolution)],
							},
							resolutionType: resolution.resolution,
							onResponseComplete: () => {
								accepted = true;
								session.acceptedMessages.add(descriptor.messageId);
								complete({ status: 'accepted' });
							},
						})
						.then(() => {
							if (!accepted) complete({ status: 'failed' });
						})
						.catch(() => complete({ status: 'failed' }));
				});
			} else {
				let failed = false;
				await this.actions.resolveConversationAction({
					conversationId: session.conversationId,
					messageId: descriptor.messageId,
					payload: {
						context: {
							...publicationAction,
							messageId: descriptor.messageId,
							resolution: { type: resolution.resolution === 'confirmed' ? 'confirm' : 'cancel' },
						},
					},
					skipSuccessOptimisticAssistantResponse: true,
					actionContentProcessor: (message) => {
						failed = !isActionExecutedSuccessfully(message);
					},
					onError: () => {
						failed = true;
					},
				});
				if (failed) return { status: 'failed' };
			}
			// This acknowledges resolution handling, not that a tool's mutation succeeded.
			return { status: 'accepted' };
		} catch {
			return { status: 'failed' };
		}
	}

	private session(sourceId: string, conversationId: string): RegisteredSession | undefined {
		const session = this.sessions.get(conversationId);
		const conversation = this.getConversation(conversationId);
		return session?.sourceId === sourceId &&
			conversation?.sourceId === sourceId &&
			conversation.status === CHAT_STORE_STATUS.idle &&
			conversation.lastAgentMessageStatus !== 'FAILED'
			? session
			: undefined;
	}

	private describe(
		session: RegisteredSession,
	): BackgroundConversationActionsDescriptor | undefined {
		const assistant = latestAssistant(this.getMessages(session.conversationId));
		if (!assistant || assistant.isAborted || assistant.content.streamingComplete === false)
			return undefined;
		const { id } = assistant.content.message.message;
		if (!id || session.lockedMessages.has(id)) return undefined;
		const pending = pendingActions(assistant);
		const supported = pending.filter(isSupported);
		if (supported.length !== 1) return undefined;
		const action = supported[0];
		// History hydration may reorder properties or add presentation metadata without
		// changing the proposed operation. Bind approval to execution identity and full data.
		const revision = JSON.stringify(
			{
				key: action.key,
				invocationId: action.invocationId,
				actionId: action.actionId ?? null,
				toolSource: action.toolSource ?? null,
				humanMessageId: action.humanMessageId ?? null,
				usesResumption: usesResumption(action),
				data: action.data,
			},
			(_key: string, value: unknown) => {
				if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
				const record = value as Record<string, unknown>;
				return Object.fromEntries(
					Object.keys(record)
						.sort()
						.map((key) => [key, record[key]]),
				);
			},
		);
		// A same-window transport must not give consumers mutable references into the chat store.
		return { messageId: id, action: JSON.parse(JSON.stringify(action)), revision };
	}

	private hasExactResolution(
		descriptor: BackgroundConversationActionsDescriptor,
		command: ResolveBackgroundConversationActionsCommandProperties,
	): boolean {
		const resolution = command.resolution;
		return (
			resolution?.invocationId === descriptor.action.invocationId &&
			(resolution.resolution === 'confirmed' || resolution.resolution === 'cancelled')
		);
	}
}
